import { useEffect, useState } from 'react'

enum SCREEN_SIZE {
  TABLET = 767,
  MOBILE = 640
}

export const MQ_BREAKPOINTS_DOWN = {
  smallDown: SCREEN_SIZE.MOBILE,
  mediumDown: SCREEN_SIZE.TABLET
} as const

const MQ_BREAKPOINTS_UP = {
  mediumUp: 768,
  smallUp: 641
} as const

type MqKeyDown = keyof typeof MQ_BREAKPOINTS_DOWN
type MqKeyUp = keyof typeof MQ_BREAKPOINTS_UP

type MqObj = {
  [key in MqKeyUp]: `@media (min-width: ${(typeof MQ_BREAKPOINTS_UP)[key]}px)`
} & {
  [key in MqKeyDown]: `@media (max-width: ${(typeof MQ_BREAKPOINTS_DOWN)[key]}px)`
}

const MQ: MqObj = {
  mediumDown: '@media (max-width: 767px)',
  mediumUp: '@media (min-width: 768px)',
  smallDown: '@media (max-width: 640px)',
  smallUp: '@media (min-width: 641px)'
}

function useMediaQuery(screenSize: SCREEN_SIZE | number): boolean {
  const query = `(max-width: ${screenSize}px)`

  const getMatches = (query: string): boolean => {
    // Prevents SSR issues
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches
    }
    return false
  }

  const [matches, setMatches] = useState<boolean>(getMatches(query))

  function handleChange() {
    setMatches(getMatches(query))
  }

  useEffect(() => {
    const matchMedia = window.matchMedia(query)

    // Triggered at the first client-side load and if query changes
    handleChange()

    // Listen matchMedia
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange)
    } else {
      matchMedia.addEventListener('change', handleChange)
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange)
      } else {
        matchMedia.removeEventListener('change', handleChange)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenSize])

  return matches
}

export { MQ, MQ_BREAKPOINTS_UP, SCREEN_SIZE, useMediaQuery }
